import React, { useMemo, useRef } from 'react'
import { IconAnalyticGeneral } from '../../../shared/icon/icon'
import { BreadcrumbView } from '../../../context/breadcrumb.context'
import { CardFullHeightScrollY } from '../../../shared/card/card-full-height-scroll-y'
import { useApiListReportGeneralOrg } from '../../../api-new/state/analytics'
import { ColumnDateTime, ColumnSelector, ColumnText } from '../../../shared/table-builder/table-columns'
import { TableAgInfinite } from '../../../shared/table-builder/table-ag-infinite'
import { LoadingState } from '../../../shared/empty-state/empty-state'
import { Menu, MenuExport, MenuTableSettingToggle } from '../../../shared/menu/menu'
import { useDownload } from '../../../shared/download/hook'
import { TableToolbarContext } from '../../../shared/table-builder/table-toolbar'
import { sourceLabel, useSourceFilter, withSourceFilter } from '../common/source-filter'
import { externalLearnersText, otherOrgCourseLabel, useExternalLearnerSummary } from './external-learners'

export const AnalyticOrgGeneral = () => {
  let tableRef = useRef()
  const handleTableSettingToggle = () => tableRef.current.toggleSideBar()

  const apiListReportGeneralOrg = useApiListReportGeneralOrg()

  const { active, download } = useDownload()

  const externalLearners = externalLearnersText(useExternalLearnerSummary())

  // The source filter (direct or LMS) goes on every request the page makes to
  // the report: its pages, the live-refresh sentinel and the export. Choosing
  // one reloads the grid in place (reloadKey) and starts the sentinel afresh;
  // the grid's datasource keeps the first onGetRows, so it reads getQuery.
  const sourceFilter = useSourceFilter()
  const toolbar = useMemo(() => ({ start: sourceFilter.control }), [sourceFilter.control])

  const handelGetRows = async (startRow, endRow, search, sortCol, sortDir) =>
    await apiListReportGeneralOrg.get(withSourceFilter(`range/${startRow}/${endRow}?search=${search}&sort-col=${sortCol}&sort-dir=${sortDir}`, sourceFilter.getQuery()))

  const handleExportStart = () => download(process.env.REACT_APP_API_ANALYTICS, withSourceFilter('api/v1/report-general-org/export-csv', sourceFilter.query))

  const HeaderMenu = () =>
    <Menu>
      <MenuTableSettingToggle onClick={handleTableSettingToggle} show />
      <MenuExport onClick={handleExportStart} disabled={active} show />
    </Menu>

  return (
    <TableToolbarContext.Provider value={toolbar}>
      <BreadcrumbView Icon={IconAnalyticGeneral} text="General analytics">
        <CardFullHeightScrollY title="General Analytics" subtitle={externalLearners} HeaderMenu={HeaderMenu}>
          {/* The grid is mounted once its column set is settled (source-filter.js): it keeps the order of the columns it was first given, and re-applies a column's `hide` on every update. */}
          {!sourceFilter.settled && <LoadingState />}
          {sourceFilter.settled && <TableAgInfinite
            reloadKey={sourceFilter.query}
            ref={tableRef}
            apiList={apiListReportGeneralOrg}
            onGetRows={handelGetRows}
            pollInterval={5000}
            sentinelServer={process.env.REACT_APP_API_ANALYTICS}
            sentinelEndpoint={withSourceFilter('api/v1/report-general-org/range/sentinel', sourceFilter.query)}
            defaultSort={{ colId: 'sessionID', sort: 'desc' }}
            emptyState={{
              Icon: IconAnalyticGeneral,
              title: 'No sessions yet',
              message: 'Sessions appear here after people use a course in the EducationXR app.'
            }}
          >
            <ColumnDateTime field="sessionStartOn" />
            <ColumnDateTime field="sessionEndOn" />
            <ColumnText field="sessionDuration" />

            <ColumnText field="stepDuration" />

            <ColumnText field="sessionID" />

            <ColumnText field="courseID" />
            <ColumnText field="courseName" />

            {sourceFilter.available && <ColumnSelector field="source" header="Source" sortable={false} selector={sourceLabel} />}

            <ColumnText field="email" />
            <ColumnText field="firstName" />
            <ColumnText field="lastName" />

            <ColumnText field="stepName" header="Step" />
            <ColumnText field="objectives" />
            <ColumnText field="objectivesCompleted" />
            <ColumnText field="attachments" />
            <ColumnText field="attachmentsCompleted" header="Attachments Viewed" />

            <ColumnText field="os" />
            <ColumnText field="platform" />
            <ColumnText field="appVersion" />
            <ColumnText field="deviceModel" />
            <ColumnText field="deviceType" />
            <ColumnText field="vrDeviceModel" />
            <ColumnText field="vrDeviceName" />
            <ColumnText field="vrDevicePresent" />
            <ColumnText field="vrDeviceTrackingOriginMode" />
            <ColumnText field="vrDeviceTrackingSpaceType" />
            <ColumnText field="vrDeviceRefreshRate" />
            <ColumnText field="graphicsCard" />
            <ColumnText field="graphicsCardMemorySize" />
            <ColumnText field="graphicsCardVendor" />
            <ColumnText field="processor" />
            <ColumnText field="processorCount" />
            <ColumnText field="processorFrequency" />
            <ColumnText field="systemMemorySize" />
            <ColumnText field="isMultiPlayer" />
            <ColumnText field="locationCountry" />
            <ColumnText field="locationRegion" />
            <ColumnText field="locationCity" />

            <ColumnSelector field="externalUser" header="Other Organization's Course" sortable selector={otherOrgCourseLabel} />

            <ColumnText field="groups" />
          </TableAgInfinite>}
        </CardFullHeightScrollY>
      </BreadcrumbView>
    </TableToolbarContext.Provider>
  )
}
